import React, { useRef } from "react";
import { Input, InputRef } from "antd";
import { message } from "antd";
import {
  API_ENDPOINTS,
  apiRequest,
  saveSessionIdToStorage,
} from "../config/api";
import { getImageUrlBySticky } from "../utils/urlUtils";
import "./MessageInput.css";
import newTopicIcon from "../assets/images/new_topic.svg";
import sendIcon from "../assets/images/send.svg";

const { TextArea } = Input;

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (text: string) => void;
  onNewTopic?: (sessionId: string) => void;
  onFocus?: () => void;
  loading: boolean;
  placeholder?: string;
  isNewConversation?: boolean;
  hasConversation?: boolean;
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  value,
  onChange,
  onSubmit,
  onNewTopic,
  onFocus,
  loading,
  placeholder = "你有什么问题可以咨询我…",
  isNewConversation = false,
  hasConversation = false,
  disabled = false,
}) => {
  const inputRef = useRef<InputRef>(null);
  const [newTopicLoading, setNewTopicLoading] = React.useState(false);
  // 键盘事件处理
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !loading) {
      e.preventDefault();
      onSubmit(value);
    }
    // Shift+Enter 允许换行，这是TextArea的默认行为
  };

  // 处理新话题 - 调用创建新会话API
  const handleNewTopic = async () => {
    if (isNewConversation) {
      message.info("已经是新话题了");
      return;
    }

    try {
      setNewTopicLoading(true);

      // 使用集中管理的API端点和请求函数
      const data = await apiRequest(API_ENDPOINTS.NEW_SESSION, {
        method: "POST",
      });

      const sessionId = data.session_id;

      // 将会话ID存储到 localStorage 中
      saveSessionIdToStorage(sessionId);

      // 如果有提供回调函数，调用它并传递新的session_id
      if (onNewTopic) {
        onNewTopic(sessionId);
      }

      // 聚焦输入框
      if (inputRef.current) {
        inputRef.current.focus();
      }

      message.success("已创建新会话");
    } catch (error) {
      console.error("创建新会话出错:", error);
      message.error("创建新会话失败，请重试");
    } finally {
      setNewTopicLoading(false);
    }
  };

  // 新话题按钮是否可用 - 考虑整体禁用状态
  const isNewTopicButtonDisabled =
    newTopicLoading || !hasConversation || disabled;

  // 检查sticky参数，如果为"1"则隐藏新话题按钮
  const sticky = getImageUrlBySticky();
  const shouldHideNewTopicButton = sticky === "1";

  return (
    <div
      className={`input-wrapper-container ${shouldHideNewTopicButton ? "sticky-welfare-container" : ""
        }`}
    >
      <div className="input-wrapper">
        <div className="input-container">
          {/* 左侧新话题按钮 - 当sticky=1时隐藏 */}
          {!shouldHideNewTopicButton && (
            <button
              className={`new-topic-button ${isNewConversation ? "active" : ""
                } ${!hasConversation || disabled ? "disabled" : ""}`}
              onClick={handleNewTopic}
              disabled={isNewTopicButtonDisabled}
              aria-label="开启新话题"
            >
              <div className="new-topic-circle">
                <img src={newTopicIcon} alt="新话题" width={20} height={20} />
              </div>
            </button>
          )}

          {/* 输入框背景 - 只应用于中间的输入区域 */}
          {/* <div
            className={`input-bg ${disabled ? "disabled" : ""}`}
            style={{ left: shouldHideNewTopicButton ? "8px" : "48px" }}
          ></div> */}

          {/* 输入框 */}
          <TextArea
            ref={inputRef}
            className="message-input"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading || disabled}
            bordered={false}
            autoSize={{ minRows: 1, maxRows: 5 }}
            style={{ resize: "none" }}
            onFocus={onFocus}
          />
        </div>

        {/* 发送按钮 - 独立定位在右下角 */}
        <button
          className="send-button send-button-fixed"
          onClick={() => onSubmit(value)}
          disabled={loading || !value.trim() || disabled}
          aria-label="发送消息"
        >
          {loading ? (
            <div className="loading-spinner"></div>
          ) : (
            <img
              src={sendIcon}
              alt="发送"
              width={20}
              height={20}
              className="send-icon"
            />
          )}
        </button>
      </div>
      <div className="disclaimer-text">以上内容均由AI生成, 仅供参考</div>
    </div>
  );
};

export default MessageInput;
